'use client'

import { Box, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { useTranslations } from 'next-intl'
import { type MouseEvent, useId, useLayoutEffect, useRef, useState } from 'react'

/** Matches the line height of the assistant's answer text. */
const LINE_HEIGHT = 1.6
/** A prompt taller than this many lines is clamped. */
const OVERFLOW_LINES = 4
/** Collapsed height in lines; the card edge cuts the fourth line near its bottom. */
const CLAMP_LINES = 3.85
/** Height in lines down to which the text is fully opaque before it starts to fade. */
const FADE_START_LINES = 2.5
/** Text opacity at the clip edge. */
const FADE_END_OPACITY = 0.3

type CollapsiblePromptProps = {
  text: string
}

/**
 * A user prompt card. A prompt taller than four lines is clamped to just under four lines that fade out towards the
 * clip edge, and clicking the card toggles the full text. Keyboard and screen-reader users get a real button that
 * stays visually hidden until it takes keyboard focus. A prompt that fits renders unclamped.
 */
function CollapsiblePrompt({ text }: CollapsiblePromptProps) {
  const t = useTranslations('features.AssistantV2')
  const textId = useId()
  const bubbleRef = useRef<HTMLDivElement>(null)
  const clampRef = useRef<HTMLParagraphElement>(null)
  const [overflowing, setOverflowing] = useState(false)
  const [expanded, setExpanded] = useState(false)

  useLayoutEffect(() => {
    const clamp = clampRef.current
    if (!clamp) return
    // scrollHeight is the full text height whether or not the clamp is applied.
    const measure = () => setOverflowing(clamp.scrollHeight > OVERFLOW_LINES * lineHeightPx(clamp))
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(clamp)
    return () => observer.disconnect()
  }, [text])

  const collapsed = overflowing && !expanded
  const toggle = () => setExpanded((value) => !value)
  const onBubbleClick = () => {
    if (endsTextSelection(bubbleRef.current)) return
    toggle()
  }
  const onButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    toggle()
  }

  return (
    <Box
      ref={bubbleRef}
      onClick={overflowing ? onBubbleClick : undefined}
      sx={(theme) => ({
        position: 'relative',
        px: 1.75,
        pt: 1.25,
        // The clipped, faded text runs to the border so the card's edge cuts through the fourth line.
        pb: collapsed ? 0 : 1.25,
        borderRadius: '12px',
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: theme.palette.mode === 'light' ? theme.shadows[1] : 'none',
        ...(overflowing && { cursor: 'pointer' })
      })}
    >
      {/* The clamp sits on the text element itself so its em units resolve against the text's own font size. */}
      <Typography
        ref={clampRef}
        id={textId}
        variant="body2"
        sx={{
          color: 'text.primary',
          lineHeight: LINE_HEIGHT,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflow: 'hidden',
          ...(collapsed && {
            maxHeight: `${CLAMP_LINES * LINE_HEIGHT}em`,
            // Only the alpha channel of a mask matters, so the fade needs no theme colour.
            maskImage: `linear-gradient(to bottom, #000 ${FADE_START_LINES * LINE_HEIGHT}em, rgba(0, 0, 0, ${FADE_END_OPACITY}) ${CLAMP_LINES * LINE_HEIGHT}em)`
          })
        }}
      >
        {text}
      </Typography>
      {overflowing && (
        <Box
          component="button"
          type="button"
          aria-expanded={expanded}
          aria-controls={textId}
          onClick={onButtonClick}
          sx={(theme) => ({
            ...visuallyHidden,
            font: 'inherit',
            '&:focus-visible': {
              clip: 'auto',
              clipPath: 'none',
              height: 'auto',
              width: 'auto',
              m: 0,
              overflow: 'visible',
              right: theme.spacing(1),
              bottom: theme.spacing(0.5),
              px: 0.75,
              py: 0.25,
              border: 1,
              borderColor: 'divider',
              borderRadius: '4px',
              bgcolor: 'background.paper',
              color: 'text.primary',
              ...theme.typography.caption,
              cursor: 'pointer'
            }
          })}
        >
          {expanded ? t('collapsePrompt') : t('expandPrompt')}
        </Box>
      )}
    </Box>
  )
}

/** The rendered line height in pixels; browsers resolve a unitless line height to px in computed style. */
function lineHeightPx(element: HTMLElement): number {
  return Number.parseFloat(window.getComputedStyle(element).lineHeight)
}

/** True when the click that just fired finished selecting text inside the bubble. */
function endsTextSelection(bubble: HTMLElement | null): boolean {
  const selection = window.getSelection()
  if (!bubble || !selection || selection.isCollapsed) return false
  return bubble.contains(selection.anchorNode) || bubble.contains(selection.focusNode)
}

export default CollapsiblePrompt
